import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="flex items-center justify-between p-6 md:p-8 border-b border-white/5">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-black tracking-tight">Trade Office</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Terms and Conditions</h1>
        <p className="text-muted-foreground mb-8">Last Updated: February 9, 2026</p>
        
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-lg">
            <strong>Company:</strong> Trade Office Solutions Limited (Company No: 16896413)
          </p>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">1. The Contract</h2>
            <p className="text-muted-foreground leading-relaxed">
              By creating an account with Trade Office Solutions Limited ("the Company," "we," "us"), you agree to these Terms and Conditions. This is a legally binding contract between us and your business.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">2. The Service (AI Quoting)</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Estimated Nature:</strong> Our platform uses AI to retrieve pricing data from third-party retailers (e.g., Screwfix). You acknowledge that these prices are <strong>estimates only</strong>.
              </li>
              <li>
                <strong className="text-foreground">Accuracy:</strong> AI is probabilistic. We do not guarantee that the product <strong>identified</strong> or the price retrieved is 100% <strong>accurate</strong>.
              </li>
              <li>
                <strong className="text-foreground">User Verification:</strong> You have a <strong>strict duty</strong> to review every line item, quantity, and price in a quote before sending it to a customer or <strong>purchasing</strong> materials. We are not liable for losses caused by your failure to verify data.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">3. Subscription & Payments</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Plans:</strong> Fees are charged according to your selected tier (£27, £50, or £150 per month).
              </li>
              <li>
                <strong className="text-foreground">Quote Limits:</strong> Each tier has a monthly quote limit. Unused quotes do not roll over to the next month.
              </li>
              <li>
                <strong className="text-foreground">Price Changes:</strong> We reserve the right to change our subscription fees. We will provide at least <strong>30 days'</strong> notice before any price increase takes effect.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">4. Third-Party Retailers & Scraping</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">No Affiliation:</strong> We are not affiliated with, endorsed by, or partnered with <strong>any retailer</strong> we scrape.
              </li>
              <li>
                <strong className="text-foreground">Service Availability:</strong> Our ability to provide prices depends on third-party websites. If a retailer blocks our AI agents or changes their website structure, the service may be temporarily unavailable. We are not liable for <strong>any such</strong> external disruptions.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">5. Limitation of Liability (The "Shield")</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">No Indirect Loss:</strong> We are not liable for any loss of profit, loss of business, or wasted labor costs resulting from an incorrect quote or a service outage.
              </li>
              <li>
                <strong className="text-foreground">Liability Cap:</strong> Our total liability to you for any claim is limited to the <strong>total amount you have paid us</strong> in subscription fees during the 12 months prior to the claim.
              </li>
              <li>
                <strong className="text-foreground">Professional Advice:</strong> Our AI is a tool, not a professional consultant. The final business decision to take a job at a specific price <strong>remains</strong> yours alone.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">6. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may not use our service to "bulk scrape" data for resale. You may not <strong>attempt</strong> to reverse-engineer our AI or scraping logic. We reserve the right to suspend your account if we detect abusive usage patterns.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">7. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              You can cancel your subscription at any time via your account settings. Access will continue until the end of your current billing period. No refunds are provided for partial months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">8. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These terms are governed by the laws of <strong>England and Wales</strong>. Any disputes will be handled exclusively by the courts of England and Wales.
            </p>
          </section>
        </div>

        {/* Back to top / home */}
        <div className="mt-12 pt-8 border-t border-white/10 flex justify-between items-center">
          <Link to="/" className="text-orange-500 hover:text-orange-400 font-medium">
            ← Back to Home
          </Link>
          <Link to="/privacy" className="text-orange-500 hover:text-orange-400 font-medium">
            Privacy Policy →
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-8 border-t border-white/5 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold">Trade Office</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-sm text-orange-500 font-medium">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-orange-500 transition-colors">
              Privacy Policy
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Trade Office Solutions Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
